import { useState } from 'react';
import { Button, Drawer, Group, MultiSelect, NumberInput, Select, Stack, Switch, TextInput } from '@mantine/core';
import type { OfferDraft } from '../../staff/catalogue';

const AVAILABILITY_OPTIONS = ['self-serve', 'sales-only', 'retired'];

// Backend TrialOffer requires a 1–365 day duration (no "unlimited"); a new offer starts here.
const DEFAULT_DURATION = 30;

interface OfferDrawerProps {
  opened: boolean;
  item: OfferDraft;
  tierSlugs: string[];
  moduleSlugs: string[];
  onSave: (o: OfferDraft) => void;
  onClose: () => void;
}

// Item ids are "<kind>:<slug>" (mirrors catalogue.ts's slugOf convention); confers picker
// options are sourced entirely from tierSlugs/moduleSlugs so a saved offer can never
// reference a tier or module that isn't part of this draft.
function toId(kind: 'tier' | 'module', slug: string): string {
  return `${kind}:${slug}`;
}

function fromId(id: string): { kind: string; slug: string } {
  const i = id.indexOf(':');
  return i === -1 ? { kind: '', slug: id } : { kind: id.slice(0, i), slug: id.slice(i + 1) };
}

export function OfferDrawer({ opened, item, tierSlugs, moduleSlugs, onSave, onClose }: Readonly<OfferDrawerProps>) {
  // Re-seed local state whenever a different draft item is opened. Adjusted during render
  // (not in an effect) per https://react.dev/learn/you-might-not-need-an-effect — avoids an
  // extra commit and the react-hooks/set-state-in-effect lint rule.
  const [prevItem, setPrevItem] = useState(item);
  const [confers, setConfers] = useState<string[]>(item.confers.map((c) => toId(c.kind as 'tier' | 'module', c.slug)));
  const [durationDays, setDurationDays] = useState<number>(item.durationDays ?? DEFAULT_DURATION);
  const [availability, setAvailability] = useState<string | null>(item.availability);
  const [oncePerOrg, setOncePerOrg] = useState(item.oncePerOrg);

  if (item !== prevItem) {
    setPrevItem(item);
    setConfers(item.confers.map((c) => toId(c.kind as 'tier' | 'module', c.slug)));
    setDurationDays(item.durationDays ?? DEFAULT_DURATION);
    setAvailability(item.availability);
    setOncePerOrg(item.oncePerOrg);
  }

  const conferOptions = [
    ...tierSlugs.map((slug) => ({ value: toId('tier', slug), label: `tier:${slug}` })),
    ...moduleSlugs.map((slug) => ({ value: toId('module', slug), label: `module:${slug}` })),
  ];

  const handleSave = () => {
    onSave({
      slug: item.slug,
      confers: confers.map(fromId),
      durationDays,
      availability,
      oncePerOrg,
    });
  };

  return (
    <Drawer opened={opened} onClose={onClose} title={`Edit offer: ${item.slug}`} position="right" size="md">
      <Stack gap="md">
        <TextInput label="Slug" value={item.slug} disabled />
        <MultiSelect
          label="Confers"
          data={conferOptions}
          value={confers}
          onChange={setConfers}
          placeholder="Pick tiers or modules"
        />
        <Select
          label="Availability"
          data={AVAILABILITY_OPTIONS}
          value={availability}
          onChange={setAvailability}
        />
        <NumberInput
          label="Duration (days)"
          min={1}
          max={365}
          value={durationDays}
          onChange={(v) => setDurationDays(typeof v === 'number' ? v : DEFAULT_DURATION)}
        />
        <Switch
          label="Once per org"
          checked={oncePerOrg}
          onChange={(e) => setOncePerOrg(e.currentTarget.checked)}
        />
        <Group justify="flex-end" gap={10}>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
